import { useCallback, useMemo } from 'react';
import type { ValueOf } from 'type-fest';

import { window } from '../../../../globals';
import useOrganizationSettings, { type OrganizationSettingsEndpoint } from '../../shared/useOrganizationSettings';
import type { CustomLicenseScan, EditingCustomLicenseScan } from '../../../../../shared/settings/customLicenseScans';

const getNewCustomLicenseScanConfig = () => ({
  id: null,
  name: '',
  matchCriteria: '',
} as EditingCustomLicenseScan);

/**
 * Hook which acts as a thin wrapper around useOrganizationSettings,
 * providing the correct endpoint, as well as simplifying the updater functions.
 */
function useCustomLicenseScanSettings() {
  const endpoint = useMemo<OrganizationSettingsEndpoint>(() =>
    `/api/organizations/${window.user?.organizationId ?? 0}/settings/integrations/custom-license-scans`, []);

  const {
    settings: customLicenseScanConfigs,
    isLoading,
    errors,
    onUpdateSettings,
    onSaveSettings,
    isSaveButtonDisabled,
  } = useOrganizationSettings<CustomLicenseScan[]>({
    endpoint,
    settingsSection: 'custom license scan',
  });

  const onAdd = useCallback(() =>
    onUpdateSettings((customLicenseScanConfigs || []).concat(getNewCustomLicenseScanConfig() as CustomLicenseScan)),
  [customLicenseScanConfigs, onUpdateSettings]);

  const onRemove = useCallback((index: number) => {
    //  Create a new array as splice will mutate the original array.
    const nextSettings = [...(customLicenseScanConfigs || [])];
    nextSettings.splice(index, 1);
    onUpdateSettings(nextSettings);
  }, [customLicenseScanConfigs, onUpdateSettings]);

  const onUpdate = useCallback((
    index: number,
    key: keyof CustomLicenseScan,
    value: ValueOf<CustomLicenseScan>,
  ) =>
    onUpdateSettings((customLicenseScanConfigs || [])
      // oxlint-disable-next-line oxc/no-map-spread -- copy on purpose: `config` is settings data owned by the settings hook
      .map((config, configIndex) => index !== configIndex ? config : ({
        ...config,
        [key]: value,
      }))),
  [customLicenseScanConfigs, onUpdateSettings]);

  return {
    isLoading,
    errors,
    onAdd,
    onRemove,
    onUpdate,
    customLicenseScanConfigs,
    onSaveSettings,
    isSaveButtonDisabled,
  };
}

export default useCustomLicenseScanSettings;
